import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useAdminData } from './AdminContext';
import { formatDateTime, getStatusBadgeClass } from './utils';
import { formatLongDate } from '../../utils/dateFormatter';

const AdminDisputesTab = () => {
  const navigate = useNavigate();
  const { disputes, updateDisputeStatus, resolveDisputeWithStatus } = useAdminData();
  
  const [disputesSearch, setDisputesSearch] = useState('');
  const [resolvingDispute, setResolvingDispute] = useState(null);
  const [newInquiryStatus, setNewInquiryStatus] = useState('');

  const filteredDisputes = useMemo(() => {
    return disputes.filter(d => {
      if (disputesSearch === '') return true;
      const searchLower = disputesSearch.toLowerCase();
      return (
        d.customer_name?.toLowerCase().includes(searchLower) ||
        d.vendor_name?.toLowerCase().includes(searchLower) ||
        d.reason?.toLowerCase().includes(searchLower) ||
        d.description?.toLowerCase().includes(searchLower)
      );
    });
  }, [disputes, disputesSearch]);

  const handleResolve = async () => {
    if (!newInquiryStatus) return;
    await resolveDisputeWithStatus(resolvingDispute.id, resolvingDispute.inquiry_id, newInquiryStatus);
    setResolvingDispute(null);
    setNewInquiryStatus('');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Dispute Management</CardTitle>
          <CardDescription>Review and resolve customer disputes</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by customer, vendor, or reason..."
              value={disputesSearch}
              onChange={(e) => setDisputesSearch(e.target.value)}
              className="pl-10 max-w-md"
            />
          </div>

          {/* Disputes Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDisputes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {disputesSearch ? 'No disputes match your search' : 'No disputes found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDisputes.map((dispute) => (
                    <TableRow key={dispute.id}>
                      <TableCell className="font-medium">{dispute.customer_name}</TableCell>
                      <TableCell className="text-gray-600">{dispute.vendor_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {dispute.reason?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600 max-w-xs truncate" title={dispute.description}>
                          {dispute.description}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeClass(dispute.dispute_status, 'dispute')}>
                          {dispute.dispute_status?.charAt(0).toUpperCase() + dispute.dispute_status?.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(dispute.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {dispute.dispute_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateDisputeStatus(dispute.id, 'under_review')}
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          )}
                          {dispute.dispute_status !== 'resolved' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => setResolvingDispute(dispute)}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Resolve
                            </Button>
                          )}
                          {dispute.inquiry_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/dashboard?tab=active-inquiries`)}
                              title="View in Active Inquiries"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resolution Dialog */}
      {resolvingDispute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Resolve Dispute</CardTitle>
              <CardDescription>Review and resolve this customer dispute by changing the inquiry status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Customer</Label>
                  <p className="font-medium">{resolvingDispute.customer_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Vendor</Label>
                  <p className="font-medium">{resolvingDispute.vendor_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Event</Label>
                  <p>{resolvingDispute.inquiry_details?.event_type} - {formatLongDate(resolvingDispute.inquiry_details?.event_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Current Inquiry Status</Label>
                  <Badge variant="outline" className="mt-1">
                    {resolvingDispute.inquiry_details?.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Dispute Reason</Label>
                  <p className="font-medium">{resolvingDispute.reason?.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-gray-600 mt-1">{resolvingDispute.description}</p>
                </div>
              </div>

              <div>
                <Label>Change Inquiry Status To:</Label>
                <Select value={newInquiryStatus} onValueChange={setNewInquiryStatus}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select new status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="accepted">Accepted / Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                    <SelectItem value="proposal_rejected">Proposal Rejected</SelectItem>
                    <SelectItem value="proposal_accepted">Proposal Accepted</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-2">
                  Changing to &quot;Completed&quot; will allow the customer to leave a review.
                </p>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={handleResolve}
                  disabled={!newInquiryStatus}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Resolve & Update Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setResolvingDispute(null); setNewInquiryStatus(''); }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default AdminDisputesTab;
